import { useState, useMemo } from 'react';
import useSWR from 'swr';
import {
  getNestedDataFetcher,
  postDataFetcher,
  deleteDataFetcher,
} from '@/utils/apis';
import { apiURL } from '@/constant/apiURL';
import { useSnackbar } from '@/hooks/global-tools';

export type WordReplaceHistoryItem = {
  original: string;
  replaced: string;
};

/**
 * 단어 바꾸기 이력 조회 훅
 */
export const useWordReplaceHistory = (documentId: string) => {
  const url = `${apiURL.TALK_FLOW_WORD_REPLACE_HISTORY}?documentId=${documentId}`;

  const {
    data: rawData,
    error,
    isLoading,
    mutate,
  } = useSWR<WordReplaceHistoryItem[]>(url, getNestedDataFetcher);

  // 데이터 가공: 중복 제거 + 최대 5개 + 최신순
  const data = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];

    // 중복 제거 (같은 original이 있으면 마지막 것만)
    const uniqueMap = new Map<string, WordReplaceHistoryItem>();

    // 최신순으로 순회하면서 중복 제거 (첫 번째로 만난 것만 유지)
    for (let i = rawData.length - 1; i >= 0; i--) {
      const item = rawData[i];
      if (!uniqueMap.has(item.original)) {
        uniqueMap.set(item.original, item);
      }
    }

    // Map을 배열로 변환하고 최신순으로 정렬 (역순)
    const uniqueArray = Array.from(uniqueMap.values()).reverse();

    // 최대 5개만 반환
    return uniqueArray.slice(0, 5);
  }, [rawData]);

  return {
    data,
    error,
    isLoading,
    mutate,
  };
};

/**
 * 단어 바꾸기 API 훅
 */
export const useWordReplace = () => {
  const [isPending, setIsPending] = useState(false);
  const { showErrorMessage } = useSnackbar();

  const mutateAsync = async (payload: {
    original: string;
    replaced: string;
    count: number;
  }) => {
    setIsPending(true);
    try {
      await postDataFetcher(apiURL.TALK_FLOW_WORD_REPLACE, payload);
    } catch (e) {
      showErrorMessage(e);
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};

/**
 * 단어 바꾸기 이력 삭제 훅
 */
export const useDeleteWordReplaceHistory = () => {
  const [isPending, setIsPending] = useState(false);
  const { showErrorMessage } = useSnackbar();

  const mutateAsync = async (payload: {
    original: string;
    replaced: string;
  }) => {
    setIsPending(true);
    try {
      await deleteDataFetcher(apiURL.TALK_FLOW_WORD_REPLACE_HISTORY, payload);
    } catch (e) {
      showErrorMessage(e);
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};
